import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';

import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import {Avatar, Box, Typography} from '@mui/material';

import {MatchTotalBar} from '../components/MatchTotalBar';
import {formatMatchScore} from '../utils/matchScoreUtils';

import {TableRowRequired} from '../../../../../components/BasicTable/BasicTable';
import {BasicTableCell, BasicTableRow} from '../../../../../components/BasicTable/BasicTableRow';
import {EmpionButton} from '../../../../../components/EmpionButton/EmpionButton';
import {Icon, IconType} from '../../../../../components/Icon/Icon';
import {TextOverflowWithTooltip} from '../../../../../components/TextOverflowWithTooltip/TextOverflowWithTooltip';
import {greenColors, orangeColors} from '../../../../../layouts/theme/designSystem';
import {MATCH_DETAILS_FROM_MATCHES, MATCH_DETAILS_RETURN_TO_PARAM} from '../../../../../lib/matchDetailsNav';
import {MatchData} from '../../../../../types/shared-recruiting-types';
import {transformDate} from '../../../../../utils/dateUtils/dateFormattingUtils';
import {MatchStatusLabel} from '../../talent-pool/MatchStatusLabel';
import {getCandidateInitials} from '../../utils/getCandidateInitials';

/** Dot + total bar: green = active pipeline; empion orange = accepted; light orange = rejected. */
const MATCH_STATUS_VISUAL: Record<string, {dot: string; barFill: string}> = {
  accepted: {dot: orangeColors[100], barFill: orangeColors[100]},
  contacted: {dot: greenColors[700], barFill: greenColors[700]},
  on_hold: {dot: greenColors[700], barFill: greenColors[700]},
  interview: {dot: greenColors[700], barFill: greenColors[700]},
  in_interview: {dot: greenColors[700], barFill: greenColors[700]},
  hired: {dot: greenColors[700], barFill: greenColors[700]},
  rejected: {dot: orangeColors[40], barFill: orangeColors[40]},
};

const statusVisualFallback = {dot: greenColors[70], barFill: greenColors[100]};

/** Full cell width so long labels (e.g. DE) fit; fixed height so primary/contained and secondary/outlined match. */
const MATCH_TABLE_ACTION_BUTTON_SX = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  height: 32,
  minHeight: 32,
  maxHeight: 32,
  boxSizing: 'border-box',
  py: 0,
  px: 0.75,
  borderRadius: '5px',
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: '16px',
  textTransform: 'none' as const,
  whiteSpace: 'nowrap',
  flexWrap: 'nowrap',
  overflow: 'hidden',
  wordBreak: 'keep-all',
  justifyContent: 'flex-start',
  alignItems: 'center',
  textAlign: 'left' as const,
  '& .MuiButton-startIcon': {
    marginLeft: 0,
    marginRight: '4px',
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
  },
  '& .MuiButton-label': {
    display: 'inline-flex',
    alignItems: 'center',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'left',
  },
  '&&.MuiButton-contained, &&.MuiButton-outlined': {
    minHeight: 32,
    maxHeight: 32,
    paddingTop: 0,
    paddingBottom: 0,
  },
};

export const MatchListRow = (match: MatchData & TableRowRequired) => {
  const navigate = useNavigate();
  const {search: listSearch} = useLocation();
  const {t: tMatches} = useTranslation('translation', {keyPrefix: 'matchesPage'});

  const matchDetailsTo = useMemo(() => {
    const raw = listSearch.startsWith('?') ? listSearch.slice(1) : listSearch;
    const qs = new URLSearchParams(raw);
    qs.set(MATCH_DETAILS_RETURN_TO_PARAM, MATCH_DETAILS_FROM_MATCHES);
    return `/company/recruiting/match/${match.resourceId}?${qs.toString()}`;
  }, [listSearch, match.resourceId]);

  const redirect = () => {
    navigate(matchDetailsTo);
  };

  const firstName = match.applicant?.personalInformation?.firstName ?? '';
  const lastName = match.applicant?.personalInformation?.lastName ?? '';
  const fullName = `${firstName} ${lastName}`.trim();
  const {dot: dotColor, barFill} = MATCH_STATUS_VISUAL[match.status] ?? statusVisualFallback;
  const overall = match.matchInformationOverview?.overallScore;
  const isAcceptedStatus = match.status === 'accepted';
  const matchActionLabel = isAcceptedStatus ? tMatches('matchReviewButton') : tMatches('matchEditButton');
  const matchActionVariant = isAcceptedStatus ? 'primary' : 'secondary';
  const matchActionIcon = isAcceptedStatus ? (
    <ThumbUpAltOutlinedIcon sx={{fontSize: '16px'}} />
  ) : (
    <Icon iconColor='currentColor' icon={IconType.Eyeball} />
  );

  return (
    <BasicTableRow rowVariant='flat'>
      <BasicTableCell>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25}}>
          <Avatar
            alt=''
            sx={{
              width: 27,
              height: 27,
              fontSize: '10px',
              fontWeight: 700,
              bgcolor: orangeColors[5],
              color: greenColors[100],
            }}
          >
            {getCandidateInitials({firstName, lastName})}
          </Avatar>
          <TextOverflowWithTooltip
            variant='body2'
            sxTypography={{fontSize: '13px', letterSpacing: '0.39px'}}
            text={fullName || '—'}
            to={matchDetailsTo}
          />
        </Box>
      </BasicTableCell>
      <BasicTableCell>
        <TextOverflowWithTooltip
          variant='body2'
          sxTypography={{fontSize: '13px', letterSpacing: '0.39px'}}
          text={match.companyJobPosting?.title || '—'}
        />
      </BasicTableCell>
      <BasicTableCell>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0}}>
          <Box sx={{width: 10, height: 10, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0}} />
          <Typography
            sx={{
              fontSize: '13px',
              lineHeight: '16px',
              letterSpacing: '0.39px',
              textTransform: 'capitalize',
              minWidth: 0,
            }}
            noWrap
          >
            <MatchStatusLabel status={match.status} />
          </Typography>
        </Box>
      </BasicTableCell>
      <BasicTableCell>
        <TextOverflowWithTooltip
          sxTypography={{fontSize: '13px', letterSpacing: '0.39px'}}
          variant='body2'
          text={match.createdAt ? transformDate(match.createdAt, 'numeric') : '-'}
        />
      </BasicTableCell>
      <BasicTableCell>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <MatchTotalBar score={overall} barFill={barFill} />
          <Typography sx={{fontSize: '13px', letterSpacing: '0.39px', textTransform: 'capitalize', minWidth: 36}}>
            {formatMatchScore(overall)}
          </Typography>
        </Box>
      </BasicTableCell>
      <BasicTableCell align='center'>
        <Typography sx={{fontSize: '13px', letterSpacing: '0.39px', textTransform: 'capitalize'}}>
          {formatMatchScore(match.matchInformationOverview?.jobMatchScore)}
        </Typography>
      </BasicTableCell>
      <BasicTableCell align='center'>
        <Typography sx={{fontSize: '13px', letterSpacing: '0.39px', textTransform: 'capitalize'}}>
          {formatMatchScore(match.matchInformationOverview?.assessmentScore)}
        </Typography>
      </BasicTableCell>
      <BasicTableCell align='center'>
        <Typography sx={{fontSize: '13px', letterSpacing: '0.39px', textTransform: 'capitalize'}}>
          {formatMatchScore(match.matchInformationOverview?.culturalMatchScore)}
        </Typography>
      </BasicTableCell>
      <BasicTableCell
        align='right'
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          verticalAlign: 'middle',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 1,
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
          }}
        >
          <EmpionButton
            size='small'
            variant={matchActionVariant}
            disableElevation
            onClick={redirect}
            aria-label={String(matchActionLabel)}
            data-tour='matches.tableAction'
            sx={MATCH_TABLE_ACTION_BUTTON_SX}
            startIcon={matchActionIcon}
          >
            {matchActionLabel}
          </EmpionButton>
        </Box>
      </BasicTableCell>
    </BasicTableRow>
  );
};
